import React, { useEffect, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Box, Flex } from "@chakra-ui/react";
import { Archive, Plus, EllipsisVertical, Pencil } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchRatesAction,
  createRateAction,
  updateRateAction,
  deleteRateAction,
} from "../../../state/actions/rateActions";
import { fetchPlansAction } from "state/actions/planActions";

import AddRateDrawer from "./components/AddRateDrawer";
import EditRateDrawer from "./components/EditRateDrawer";
import DeleteRateModal from "./components/DeleteRateModal";

import { useCustomNotification } from "components/Toasts/OpenNotification";

const KilometerRatesManagement = () => {
  const dispatch = useDispatch();
  const notify = useCustomNotification();
  const { rates, loading } = useSelector((state) => state.rates);

  const [isAddDrawerOpen, setAddDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setEditDrawerOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);

  useEffect(() => {
    dispatch(fetchRatesAction());
  }, [dispatch]);

  const openAddDrawer = () => setAddDrawerOpen(true);
  const closeAddDrawer = () => setAddDrawerOpen(false);

  const openEditDrawer = (rate) => {
    setSelectedRate(rate);
    setEditDrawerOpen(true);
  };

  const openDeleteModal = (rate) => {
    setSelectedRate(rate);
    setDeleteModalOpen(true);
  };

  const handleAddRate = async (data) => {
    try {
      await dispatch(createRateAction(data));
      closeAddDrawer();
      notify({
        type: "success",
        message: "Rate Added",
        description: "Kilometer rate added successfully.",
      });

      dispatch(fetchPlansAction());
    } catch (err) {
      notify({ type: "error", message: "Error", description: "Failed to add rate." });
    }
  };

  const handleUpdateRate = async (updatedRate) => {
    try {
      await dispatch(updateRateAction(updatedRate.id, updatedRate));
      setEditDrawerOpen(false);
      notify({
        type: "success",
        message: "Rate Updated",
        description: "Kilometer rate updated successfully.",
      });

      dispatch(fetchPlansAction());
    } catch (err) {
      notify({ type: "error", message: "Error", description: "Failed to update rate." });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteRateAction(selectedRate.id));
      setDeleteModalOpen(false);
      notify({
        type: "success",
        message: "Rate Deleted",
        description: "Kilometer rate deleted successfully.",
      });

      dispatch(fetchPlansAction());
    } catch (err) {
      notify({ type: "error", message: "Error", description: "Failed to delete rate." });
    }
  };

  const generateMenuItems = (row) => [
    { icon: <Pencil size={16} />, text: "Edit Rate", onClick: () => openEditDrawer(row.original) },
    { icon: <Archive size={16} />, text: "Delete Rate", onClick: () => openDeleteModal(row.original) },
  ];

  const columnsData = [
    { Header: "Rate per KM", accessor: "rate_per_km" },
    { Header: "Weight Divisor", accessor: "weight_divisor" },
    { Header: "Minimum Fee", accessor: "minimum_fee" },
    {
      Header: "Created At",
      accessor: "created_at",
      Cell: ({ value }) => new Date(value).toLocaleString(),
    },
    {
      Header: "Updated At",
      accessor: "updated_at",
      Cell: ({ value }) => new Date(value).toLocaleString(),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={generateMenuItems(row)}
        />
      ),
    },
  ];

  return (
    <Box>
      <Flex justifyContent="flex-end" mb={4} pt={{ base: "120px", md: "25px" }}>
        {loading ? (
          <Skeleton width="140px" height="40px" borderRadius="md" />
        ) : (
          <GradientButton icon={<Plus size={16} />} height="40px" mr={5} onClick={openAddDrawer}>
            Add Kilometer Rate
          </GradientButton>
        )}
      </Flex>

      <ReusableTable columnsData={columnsData} tableData={rates} isLoading={loading} />

      <AddRateDrawer isOpen={isAddDrawerOpen} onClose={closeAddDrawer} onAddRate={handleAddRate} />

      <EditRateDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        rate={selectedRate}
        onUpdateRate={handleUpdateRate}
      />

      <DeleteRateModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        rate={selectedRate}
      />
    </Box>
  );
};

export default KilometerRatesManagement;
